import React from 'react';
import { Link } from 'react-router-dom';
import HtmlContent from '../../../components/common/HtmlContent';
import useHashScroll from '../../../hooks/useHashScroll';

/**
 * DiscoveryPage Component
 * 
 * Displays all problem discovery tools content like the legacy HTML site
 */
const DiscoveryPage = () => {
  // Use the hash scroll hook to scroll to the correct section
  useHashScroll();
  
  // Discovery intro content from the legacy HTML
  const discoveryIntroHtml = `
    <p>
    If there is a beginning of the Product R&D process, discovering problems is it. 
    </p>
    
    <p>
    There might already be a list of problems, or vague idea of a problem, or we may wish to take the time to discover unforeseen problems to help de-risk future challenges. 
    </p>
        
    <p>
    Diving in deep during this phase will help ensure the right problems are being prioritised in the next phase. 
    </p>
  `;

  return (
    <>
      {/* Discovery Intro */}
      <div className="tool-overview-container">
        <div className="tool-breadcrumb-container">
          <div className="tool-breadcrumb-wrap">
            <div className="tool-breadcrumb-icon">
              <i className="fas fa-bullhorn fa-2x"></i>
            </div>
            <div className="tool-breadcrumb-nav">
              <Link to="/research">Research Tools</Link>
              &nbsp;&nbsp;
              <i className="fas fa-angle-double-right"></i>
              &nbsp;&nbsp;
              <Link to="/problem">Problem</Link>
            </div>
            <div className="tool-breadcrumb-title">
              <Link to="/problem/discovery">Discovery</Link>
            </div>
          </div>
        </div>
        <div className="tool-overview-para">
          <HtmlContent html={discoveryIntroHtml} containerClass="" />
        </div>
        <div className="tool-section-container">
          <div className="tool-section-wrap">
            <div className="tool-section-title">Section Tools</div>
          </div>
          <div className="tool-section-list">
            <div><Link className="tool-section-cta" to="#problem-log">Problem Log</Link></div>
            <div><Link className="tool-section-cta" to="#workshop">Problem Workshop</Link></div>
            <div><Link className="tool-section-cta" to="#problem-interview">Problem Interview</Link></div>
            <div><Link className="tool-section-cta" to="#5-whys">5 Whys</Link></div>
            <div><Link className="tool-section-cta" to="#sailboat">Sailboat</Link></div>
          </div>
        </div>
      </div>

      {/* Problem Log Section */}
      <main className="case-study">
        <span className="case-tool-title" id="problem-log">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="problem-log">
            <span className="tool-title-number">
              <Link to="#problem-log">Problem Log</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/problem">Problem</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/problem/discovery">Discovery</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#problem-log"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-problem-log" 
            id="tab-problem-log-overview" 
            defaultChecked
          />
          <label htmlFor="tab-problem-log-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              <div className="tool-table-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th colSpan="3">Tool Info</th>
                    </tr>
                    <tr>
                      <td>Category</td>
                      <td>Discovery</td>  
                    </tr>
                    <tr>
                      <td>Updated</td>
                      <td>10/08/20</td>
                    </tr>
                    <tr>
                      <td>Templates</td>
                      <td>
                        <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                        <a href="/problem/discovery/problem-log.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                        <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                        <a href="https://miro.com/app/board/o9J_kpGUsJk=/" className="link-table">Miro</a>
                      </td>
                    </tr>
                    <tr>
                      <td>Creator</td>
                      <td>N/A</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="tool-opening">
                <p>
                  A problem log is a simple way of capturing previously prioritised problems, and tracking problems that should be looked at in more detail in the future.
                </p>
                <p>
                  Quite often by logging these problems they start to overlap and multiple problems can even be solved by the same solution. 
                </p>
                <p>
                  As the log is at the beginning of the process it is pre-research, and therefore will contain a lot of assumptions. 
                </p>
                <p>
                  <span className="press">Note: </span>When these problems have either been tackled or dismissed, simply 'archive' them for future reference. 
                </p>
                <p>
                  The template uses a problem focussed 5 Ws model as a means of logging and clarifying problems. By using the 5 Ws, we can quickly assess and understand the problem. 
                </p>
                <p>               
                  A simple Now / Next / Later system then helps identify what is being worked on, what will be next and future problems to be looked at. 
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.mindtools.com/pages/article/newPPM_77.htm" className="link-table">Problem Definition Process</a><br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.atlassian.com/team-playbook/plays/problem-framing" className="link-table">Problem Framing</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-problem-log" 
            id="tab-problem-log-template"
          />
          <label htmlFor="tab-problem-log-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kpGUsJk=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Problem Log template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Problem Workshop Section */}
      <main className="case-study">
        <span className="case-tool-title" id="workshop">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="workshop">
            <span className="tool-title-number">
              <Link to="#workshop">Workshop</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/problem">Problem</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/problem/discovery">Discovery</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#workshop"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-workshop" 
            id="tab-overview" 
            defaultChecked
          />
          <label htmlFor="tab-overview">Overview</label>
          <div className="tab">
            <div className="tool-opening">
              <p>
                A problem framing workshop gathers stakeholders and other contributors for structured session to prioritise what problems should be looked at in more detail.
              </p>
              <p>
                There are multiple tools that could be implemented during the workshop, but for the purposes of an example, the following have been included in this section under the process tab;
              </p>
              <table id="tool-table">
                <tbody>
                  <tr className="table-section-tools">
                    <td>Tool</td>
                    <td>Description</td>
                    <td>Template</td>
                  </tr>
                  <tr>
                    <td><a href="/synthesis/problem-reframing/#concept" className="link-table">Problem Reframing</a></td>
                    <td>Observing and studying the problem from a different perspective.</td>
                    <td>
                      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                      <a href="/empathise/investigate/research-canvas.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                      <a href="https://miro.com/app/board/o9J_kois3So=/" className="link-table">Miro</a>
                    </td>
                  </tr>
                  <tr>
                    <td><a href="/problem/framing/#problem-statement" className="link-table">Problem Statement</a></td>
                    <td>Outline the scope of the project, including tools and resources.</td>
                    <td>
                      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                      <a href="/problem/framing/problem-statement.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                      <a href="https://miro.com/app/board/o9J_kpeg0X8=/" className="link-table">Miro</a>
                    </td>
                  </tr>
                  <tr>
                    <td><a href="/problem/prioritisation/#priority-matrix" className="link-table">Priority Matrix</a></td>
                    <td>Visually plot problems in order to proritise which should be worked on.</td>
                    <td>
                      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                      <a href="/problem/prioritisation/priority-matrix.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                      <a href="https://miro.com/app/board/o9J_kpPWRDY=/" className="link-table">Miro</a>
                    </td>
                  </tr>
                  <tr>
                    <td><a href="/problem/prioritisation/#nuf-test" className="link-table">NUF Test</a></td>
                    <td>Add some quantitantive data to the problem to help prioritise the best problem to work on.</td>
                    <td>
                      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                      <a href="/problem/prioritisation/nuf-test.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                      <a href="https://miro.com/app/board/o9J_kpTbPcI=/" className="link-table">Miro</a>
                    </td>
                  </tr>
                  <tr>
                    <td><a href="/problem/framing/#proto-persona" className="link-table">Proto-Persona</a></td>
                    <td>Have more focus on speed and efficiency when outlining the project.</td>
                    <td>
                      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                      <a href="/problem/framing/proto-persona.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                      <a href="https://miro.com/app/board/o9J_kpd1bW8=/" className="link-table">Miro</a>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p>
                Using our problem log, we can begin to discuss each problem one by one, as well as any others that have been brought up in the discussion and set us up to begin the process of problem discovery.
              </p>
              <p>
                <span className="press">Note: </span>Depending on the type and size of a team, a problem discovery workshop can also include further steps to dive deeper in to how the problems align to stakeholders and the business goals. 
              </p>
              <div className="tool-further-container">
                <div className="tool-further-wrap">
                  <table id="tool-table">
                    <tbody>
                      <tr>
                        <th>Further Resources</th>
                      </tr>
                      <tr>
                        <td>    
                          <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                          <a href="https://www.mightybytes.com/blog/problem-framing-workshop-template/" className="link-table">Solve the Right Problems</a><br />
                          <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                          <a href="https://wearebrightful.com/how-to/define-problem-before-solution/" className="link-table">How to define the problem before jumping to solutions</a><br />
                          <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                          <a href="https://medium.com/design-sprint-academy/how-to-prioritise-ideas-with-problem-framing-part-1-ideas-30-10-3-8ff9129268b" className="link-table">How to prioritise ideas with Problem Framing?</a>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-workshop" 
            id="tab-process"
          />
          <label htmlFor="tab-process">Templates</label>
          <div className="tab">
            <p>
              Before we define the problem statement, this is a good opportunity to reframe the problem in order to look at it from a different perspective. 
            </p>
            <h4>Reframe Template</h4>
            <div>
              <iframe 
                className="miro_embed" 
                src="https://miro.com/app/embed/o9J_kpPaIPU=/?&pres=1" 
                frameBorder="0" 
                scrolling="no" 
                allowFullScreen
                title="Reframe Template"
              ></iframe>
            </div>
            <p>
              Let's then define the problem as a statement in order to create consistency and clarify the problem to the team. 
            </p>
            <h4>Problem Statement Template</h4>
            <div>
              <iframe 
                className="miro_embed" 
                src="https://miro.com/app/embed/o9J_kpeg0X8=/?&pres=1" 
                frameBorder="0" 
                scrolling="no" 
                allowFullScreen
                title="Problem Statement Template"
              ></iframe>
            </div>
            <p>
              With the problems defined as statements we can start to add some data to them in order to help prioritise. A good tool to do this is a NUF test - this works as a means of adding a form of quantitative analysis to the problem, creating an NUF score that will help with prioritising.
            </p> 
            <h4>NUF Test Template</h4>
            <div>
              <iframe 
                className="miro_embed" 
                src="https://miro.com/app/embed/o9J_kpTbPcI=/?&pres=1" 
                frameBorder="0" 
                scrolling="no" 
                allowFullScreen
                title="NUF Test Template"
              ></iframe>
            </div>
            <p>
              We can then go through them one by one, grouping and prioritising them using The Priority Matrix which helps to visualise the cost/benefit of the problem and gain consensus.
            </p>
            <h4>Priority Matrix Template</h4>
            <div>
              <iframe 
                className="miro_embed" 
                src="https://miro.com/app/embed/o9J_kpPWRDY=/?&pres=1" 
                frameBorder="0" 
                scrolling="no" 
                allowFullScreen
                title="Priority Matrix Template"
              ></iframe>
            </div>
            <p>
              You can also use this workshop session to create Proto-Personas, with time set aside to create a few each, and then voting and even merging them in order to get a better understanding of the type of person who could be experiencing the problem.
            </p>   
            <h4>Proto-Persona Template</h4>
            <div>
              <iframe 
                className="miro_embed" 
                src="https://miro.com/app/embed/o9J_kpd1bW8=/?&pres=1" 
                frameBorder="0" 
                scrolling="no" 
                allowFullScreen
                title="Proto-Persona Template"
              ></iframe>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-workshop" 
            id="tab-tips"
          />
          <label htmlFor="tab-tips">Tips</label>
          <div className="tab">
            <ul className="bullet-tk">
              <li>Contributors should create their Problem Statements before the session as to keep a consistent format and discussion.</li>
              <li>Don't choose a problem too big - the resource required may make it unfeasible.</li>
              <li>Don't choose a problem too small as it might only ever reach a very small audience at best.</li>
              <li>A workshop helps to quickly cover a lot of ground and aligns focus, choosing the right problem and saving resource.</li>
              <li>Include as many relevant diverse stakeholders as possible - different perspectives will help with understanding the problem and ultimately solving it.</li>
              <li>Love the problem, not the solution.</li>
              <li>By prioritising the problems, it will help further down the line when research helps to further validate if a problem is worth solving. If not, go back and look at the next problem.</li>
              <li>The length of the workshop isn't set in stone, but expect to set aside half a day to a day.</li>
            </ul>
          </div>
        </div>
      </main>

      {/* Problem Interview Section */}
      <main className="case-study">
        <span className="case-tool-title" id="problem-interview">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="problem-interview">
            <span className="tool-title-number">
              <Link to="#problem-interview">Problem Interview</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/problem">Problem</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/problem/discovery">Discovery</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#problem-interview"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-problem-interview" 
            id="tab-problem-interview-overview" 
            defaultChecked
          />
          <label htmlFor="tab-problem-interview-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              <div className="tool-table-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th colSpan="3">Tool Info</th>
                    </tr>
                    <tr>
                      <td>Category</td>
                      <td>Discovery</td>
                    </tr>
                    <tr>
                      <td>Updated</td>
                      <td>10/08/20</td>
                    </tr>
                    <tr>
                      <td>Templates - Sheet</td>
                      <td>
                        <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                        <a href="/problem/discovery/problem-interview-sheet.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                        <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                        <a href="https://miro.com/app/board/o9J_kpWNnvM=/" className="link-table">Miro</a>
                      </td>
                    </tr>
                    <tr>
                      <td>Templates - Script</td>
                      <td>
                        <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                        <a href="/problem/discovery/problem-interview-script.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                        <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                        <a href="https://miro.com/app/embed/o9J_kpW66lY=/?pres=1&frameId=3074457348884717433" className="link-table">Miro</a>
                      </td>
                    </tr>
                    <tr>
                      <td>Creator</td>
                      <td><a href="">Ash Maurya</a></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="tool-opening">
                <h4>Overview</h4>
                <p>
                  Problems can be found anywhere but listening to stakeholders, clients, colleagues, team members and of course users we can surface the bigger more important problems - the problems those closest to the service or product are experiencing either in their life, or with the specific product.
                </p>
                <p>
                  Additionally, this is a great starting point when looking to develop entirely new products from scratch when there isn't a need for a focus, or speculatively researching in order to find opportunities. 
                </p>
                <p>
                  A Problem Interview is much like a normal User Interview in format and approach, except we're trying to uncover problems rather than validating assumptions. We can use this format first to find problems that can then be prioritised and validated further down the line during the 'empathise' phase.
                </p>
                <div className="tool-further-container">
                  <div className="tool-further-wrap">
                    <table id="tool-table">
                      <tbody>
                        <tr>
                          <th>Further Resources</th>
                        </tr>
                        <tr>
                          <td>
                            <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                            <a href="https://blog.leanstack.com/the-updated-problem-interview-script-and-a-new-canvas-1e43ff267a5d" className="link-table">Find Better Problems Worth Solving with the Customer Forces Canvas</a><br />
                            <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                            <a href="https://university.hygger.io/en/articles/1811692-problem-interview" className="link-table">How to conduct and analyze a problem interview?</a><br />
                            <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                            <a href="https://debane.org/franck/problem-interview-template/" className="link-table">Problem Interview Template</a>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-problem-interview" 
            id="tab-problem-interview-script"
          />
          <label htmlFor="tab-problem-interview-script">Problem Interview Script</label>
          <div className="tab tab-tools">
            <p>
              The same tools that would be used during the User Interview stage can be used here, except for ensuring the questions themselves are skewed from a discovery perspective - the Problem Interview script (v2) created by Ash Maurya is particularly helpful when crafting the interview.
            </p>
            <div>
              <iframe 
                className="miro_embed" 
                width="100%" 
                height="640" 
                src="https://miro.com/app/embed/o9J_kpW66lY=/?&pres=1" 
                frameBorder="0" 
                scrolling="no" 
                allowFullScreen
                title="Problem Interview Script"
              ></iframe>
            </div>
          </div>
            
          <input 
            type="radio" 
            name="tabs-problem-interview" 
            id="tabs-problem-interview-sheet"
          />
          <label htmlFor="tabs-problem-interview-sheet">Problem Interview Sheet</label>
          <div className="tab tab-tools">
            <p>
              We can also use the 'Problem Interview Sheet' as an aid to ensure we are collecting the responses we need, and also as a means to summarise the interview for sharing with the team. We are mainly trying to learn the following;
            </p>
            <ul className="bullet-tk">
              <li className="bullet-tk">How big a problem is it for the user and is it worth solving for them?</li>
              <li className="bullet-tk">How often is the problem occurring?</li>
              <li className="bullet-tk">How are they currently solving the problem? What solution have they found?</li>
              <li className="bullet-tk">How have they been looking for the solution? This is particularly helpful with marketing.</li>
              <li className="bullet-tk">Are there any further related problems that can also be solved? Or other problems that could be future opportunities.</li>
            </ul>
            <div>
              <iframe 
                className="miro_embed" 
                width="100%" 
                height="640" 
                src="https://miro.com/app/embed/o9J_kpWNnvM=/?&pres=1" 
                frameBorder="0" 
                scrolling="no" 
                allowFullScreen
                title="Problem Interview Sheet"
              ></iframe>
            </div>
          </div>
        </div>
      </main>

      {/* 5 Whys Section */}
      <main className="case-study">
        <span className="case-tool-title" id="5-whys">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="5-whys">
            <span className="tool-title-number">
              <Link to="#5-whys">5 Whys</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/problem">Problem</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/problem/discovery">Discovery</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#5-whys"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-5-whys" 
            id="tab-5-whys-overview" 
            defaultChecked
          />
          <label htmlFor="tab-5-whys-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              <div className="tool-table-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th colSpan="3">Tool Info</th>
                    </tr>
                    <tr>
                      <td>Category</td>
                      <td>Discovery</td>  
                    </tr>
                    <tr>
                      <td>Updated</td>
                      <td>10/08/20</td>
                    </tr>
                    <tr>
                      <td>Templates</td>
                      <td>
                        <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                        <a href="/problem/discovery/5-whys.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                        <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                        <a href="https://miro.com/app/board/o9J_kpXATWs=/" className="link-table">Miro</a>
                      </td>
                    </tr>
                    <tr>
                      <td>Creator</td>
                      <td><a href="https://en.wikipedia.org/wiki/Five_whys">Sakichi Toyoda</a></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="tool-opening">
                <p>
                  Another form of Problem Discovery is using the 5 Whys technique (also called the Root Cause analysis). We begin with a problem that has emerged, or what we think is a problem, and then through asking Why, can begin to understand the root cause of the problem rather than the symptoms. 
                </p>
                <p>
                  As we push through, we begin to remove assumptions, blame, and prevent the immediate solutions trap. The core problem is then a lot clearer for everyone to understand and ensure we are tracking the right problem. 
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://kanbanize.com/lean-management/improvement/5-whys-analysis-tool" className="link-table">5 Whys: The Ultimate Root Cause Analysis Tool</a><br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.mindtools.com/pages/article/newTMC_5W.htm" className="link-table">Getting to the Root of a Problem Quickly</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-5-whys" 
            id="tab-5-whys-template"
          />
          <label htmlFor="tab-5-whys-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kpXATWs=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="5 Whys template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Sailboat Section */}
      <main className="case-study">
        <span className="case-tool-title" id="sailboat">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="sailboat">
            <span className="tool-title-number">
              <Link to="#sailboat">Sailboat</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/problem">Problem</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/problem/discovery">Discovery</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#sailboat"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-sailboat" 
            id="tab-sailboat-overview" 
            defaultChecked
          />
          <label htmlFor="tab-sailboat-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              <div className="tool-table-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th colSpan="3">Tool Info</th>
                    </tr>
                    <tr>
                      <td>Category</td>
                      <td>Discovery</td>
                    </tr>
                    <tr>
                      <td>Updated</td> 
                      <td>10/08/20</td>
                    </tr>
                    <tr>
                      <td>Templates</td>
                      <td>
                        <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                        <a href="/problem/discovery/sailboat.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                        <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                        <a href="https://miro.com/app/board/o9J_kpKGzwI=/" className="link-table">Miro</a>
                      </td>
                    </tr>
                    <tr>
                      <td>Creator</td>
                      <td><a href="https://retrospectivewiki.org/index.php?title=Sailboat">Luke Hohmann</a></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="tool-opening">
                <h4>Overview</h4>
                <p>
                  The Sailboat template is usually used for Scrum retrospectives, but it can also provide a way to analyse the strengths of a business/team (the wind), what the goal and objectives are (the beach), what could be holding things back (the anchor) and what future risks lie ahead (the rocks).
                </p>
                <p>
                  By undertaking this retrospective, it can uncover future problems that may exist for the business and user.
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.atlassian.com/blog/jira-software/5-fun-sprint-retrospective-ideas-templates" className="link-table">5 fun sprint retrospective ideas with templates</a><br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://beingagile.co.uk/sailboat-retrospective-step-by-step-guide-workshop-video-and-examples-gallery-being-agile/" className="link-table">Retrospective Sailing Game</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-sailboat" 
            id="tab-sailboat-template"
          /> 
          <label htmlFor="tab-sailboat-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kpKGzwI=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Sailboat Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Note: Now all tools are implemented in the correct order:
          1. Problem Log
          2. Workshop
          3. Problem Interview
          4. 5 Whys
          5. Sailboat */}
    </>
  );
};

export default DiscoveryPage; 